import * as React from 'react';
import '../styles/MarketingPages.css';
import certificationConsultation from '../assets/certification-consultation.svg';
import { inquiryApi } from '../services/inquiryApi';

const MailIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M4 6h16v12H4z" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 21s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12z" />
    <circle cx="12" cy="9" r="2.4" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8.5 4.5 6.2 6.8c-.7.7-.8 1.8-.3 2.7 1.9 3.8 4.8 6.7 8.6 8.6.9.5 2 .4 2.7-.3l2.3-2.3-4-4-2 2c-1.7-.9-3.1-2.3-4-4l2-2z" />
  </svg>
);

const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidMobile = (phone: string): boolean => {
  const phoneFormatRegex = /^\+?[0-9\s\-()]{10,20}$/;
  if (!phoneFormatRegex.test(phone)) return false;
  const digitsOnly = phone.replace(/\D/g, '');
  return digitsOnly.length >= 10 && digitsOnly.length <= 15 && !/^(\d)\1+$/.test(digitsOnly);
};

type ContactShowcaseSectionProps = {
  id?: string;
};

export const ContactShowcaseSection: React.FC<ContactShowcaseSectionProps> = ({ id }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [statusMessage, setStatusMessage] = React.useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (statusMessage) {
      setStatusMessage(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedPhone = formData.phone.trim();
    const trimmedMessage = formData.message.trim();

    if (!trimmedName) {
      setStatusMessage({ type: 'error', text: 'Please enter your name.' });
      return;
    }

    if (!trimmedEmail) {
      setStatusMessage({ type: 'error', text: 'Please enter your email address.' });
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setStatusMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }

    if (!trimmedPhone) {
      setStatusMessage({ type: 'error', text: 'Please enter your mobile number.' });
      return;
    }

    if (!isValidMobile(trimmedPhone)) {
      setStatusMessage({
        type: 'error',
        text: 'Please enter a valid mobile number (10 to 15 digits, e.g. +91 9876543210 or 9876543210).',
      });
      return;
    }

    if (!trimmedMessage) {
      setStatusMessage({ type: 'error', text: 'Please enter your message.' });
      return;
    }

    setIsSubmitting(true);
    setStatusMessage(null);

    const result = await inquiryApi.submitInquiry({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone,
      message: trimmedMessage,
    });

    setIsSubmitting(false);

    if (result.success) {
      setStatusMessage({
        type: 'success',
        text: 'Thank you for reaching out! Your inquiry has been submitted successfully. Our team will contact you soon.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } else {
      setStatusMessage({
        type: 'error',
        text: result.error || 'Failed to submit your inquiry. Please try again.',
      });
    }
  };

  return (
    <section id={id} className="marketing-page contact-page-modern contact-page-showcase">
      <section className="contact-showcase-shell">
        <header className="contact-showcase-heading">
          <h1>
            If You Need Further Information,
            <span>Please Don't Hesitate To Ask.</span>
          </h1>
        </header>

        <div className="contact-showcase-info-cards" id="contact-details">
          <article className="contact-showcase-info-card">
            <span className="contact-showcase-icon-ring">
              <MailIcon />
            </span>
            <h3>Email Us</h3>
            <p>info@certificationiso.com</p>
            <p>info@certificationiso.com</p>
          </article>

          <article className="contact-showcase-info-card">
            <span className="contact-showcase-icon-ring">
              <PinIcon />
            </span>
            <h3>Our Address</h3>
            <p>5/346, Sector-5, Jankipuram Ext. Lucknow - 226031</p>
          </article>

          <article className="contact-showcase-info-card">
            <span className="contact-showcase-icon-ring">
              <PhoneIcon />
            </span>
            <h3>CertificationISO</h3>
            <p>Certification guidance, documentation support, and audit-readiness coordination.</p>
          </article>
        </div>

        <div className="contact-showcase-main">
          <div className="contact-showcase-visual">
            <img src={certificationConsultation} alt="CertificationISO consultation desk" className="contact-showcase-image" />
          </div>

          <div className="contact-showcase-form-card">
            <form
              className="contact-showcase-form"
              onSubmit={handleSubmit}
              noValidate
              aria-busy={isSubmitting}
            >
              {statusMessage && (
                <div
                  className={`alert ${statusMessage.type === 'success' ? 'alert-success' : 'alert-error'}`}
                  role="alert"
                >
                  {statusMessage.text}
                </div>
              )}

              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your Name"
                aria-label="Your Name"
                autoComplete="name"
                disabled={isSubmitting}
                required
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Your Email"
                aria-label="Your Email"
                autoComplete="email"
                disabled={isSubmitting}
                required
              />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Your Phone"
                aria-label="Your Phone"
                autoComplete="tel"
                disabled={isSubmitting}
                required
              />
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Your Message"
                aria-label="Your Message"
                disabled={isSubmitting}
                required
              />
              <button
                type="submit"
                className="btn btn-primary btn-large request-submit request-submit-modern"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending Message...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </section>
  );
};

const ContactPage: React.FC = () => <ContactShowcaseSection />;

export default ContactPage;

